import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { defaultValues, useReviewForm } from "./hooks/useReviewForm";
import ReviewSkeleton from "./components/ReviewSkeleton";
import ReviewWizard, { STEP_CONFIG, isDeeplyEmpty } from "./components/ReviewWizard";
import { CandidateService } from "@/services/candidateService";
import { JobService } from "@/services/jobService";
import { mapApiToForm } from "./utils/mapApiToForm";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import { requiredLanguageNames } from "@/utils/languageRequirements";
import { Error } from "@/components/shared/Error";
import { loadDefaultCurrency, getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import toast from "react-hot-toast";
import { buildQuestionAnswers } from "@/utils/screeningAnswers";

const BC_CHANNEL = "candidate_sync";

const ConfirmDetails = () => {
  const location = useLocation();
  const fromPath = location.state?.fromPath;
  const application_id = location.state?.application_id;
  const job_id = location.state?.job_id;
  const ai_consent = location.state?.ai_consent ?? false;
  const { candidateId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [mandatoryFields, setMandatoryFields] = useState([]);
  // Manual screening questions configured on this job, from the review detail
  // (empty when the job has none).
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  // "partial" when the resume couldn't be fully parsed (backend _fallback path).
  const [extractionQuality, setExtractionQuality] = useState("full");
  const [currencySign, setCurrencySign] = useState(getDefaultCurrencyFallback().currencySign);
  // Tenant-curated picker options for the languages form, straight off the
  // review response (public endpoint data — passed into the rows editor so it
  // never needs the authenticated settings fetch).
  // The job's own required languages, in two shapes for two different jobs.
  // The NAMES lock rows in the editor; the ROWS carry the levels the schema
  // needs, because only the languages the job itself levelled may have a blank
  // level refused. Deriving the second from the first loses exactly that.
  const [lockedLanguages, setLockedLanguages] = useState([]);
  const [jobRequiredLanguages, setJobRequiredLanguages] = useState([]);

  const onSubmit = async (formData) => {
    // Authoritative gate is server-side; this blocks the obvious case up front.
    const answers = form.methods.getValues("screening_answers") || {};
    const missing = (screeningQuestions || []).filter(
      (q) => q.is_required && !String(answers[q.id] ?? "").trim()
    );
    if (missing.length > 0) {
      missing.forEach((q) =>
        form.methods.setError(`screening_answers.${q.id}`, {
          type: "manual",
          message: "This question is required.",
        })
      );
      form.setStep(STEP_CONFIG.length - 1);
      toast.error("Please answer all required questions before submitting.");
      return;
    }
    // One definition, both candidate pages — and it sends the blank pairs.
    // See `utils/screeningAnswers.js`: an omitted pair means "leave the old
    // answer alone", so filtering the blanks out made clearing an optional
    // answer impossible.
    const question_answers = buildQuestionAnswers(screeningQuestions, answers);

    setIsFormSubmit(true);
    setSubmitError("");
    try {
      const response = await CandidateService.confirmCandidateDetail(
        candidateId,
        { ...formData, question_answers },
        application_id,
        ai_consent
      );

    // `email_masked` is null when the job has its apply email step off
    // (the application is already submitted, no link was mailed) — branch on
    // what the server reports rather than interpolating a null into the toast.
    const emailRequired = response?.email_confirmation_required !== false;
    const inboxes = [response?.email_masked, response?.alternate_email_masked].filter(Boolean).join(" and ");

    toast.success(
      emailRequired && inboxes
        ? `${response.message} (${inboxes})`
        : response?.message
    );

      try {
        const bc = new BroadcastChannel(BC_CHANNEL);
        bc.postMessage({ type: "TABLE_REFRESH" });
        bc.close();
      } catch (_) { }

      navigate("/user/job-listing");
    } catch (err) {
      const msg =
        err.response?.data?.message ??
        err.response?.data?.error ??
        "Submission failed. Please try again.";

      toast.error(msg);
      setSubmitError(msg);
    } finally {
      setIsFormSubmit(false);
    }
  };

  const form = useReviewForm({ defaultValues, onSubmit, mandatoryFields, requiredLanguages: jobRequiredLanguages });

  useEffect(() => {
    if (!candidateId) return;
    fetchReviewDetail();
    fetchJobFieldConfig();
  }, [candidateId]);

  const fetchJobFieldConfig = async () => {
    try {
      const response = await JobService.fetchJobFieldConfig(job_id);
      if (response.success && response.data) {
        const allMandatory = [...(response.data.always_mandatory || []).map((f) => f.key), ...(response.data.configured_mandatory || []).map((f) => f.key)];
        setMandatoryFields(allMandatory);
      }
    } catch (err) {
      console.error("Failed to fetch job field config:", err);
    }
  };

  const fetchReviewDetail = async () => {
    try {
      setIsLoading(true);
      const [data] = await Promise.all([
        CandidateService.reviewCandidateDetail(candidateId, application_id),
        loadDefaultCurrency(),
      ]);
      setCurrencySign(data?.currency_sign ?? getDefaultCurrencyFallback().currencySign);
      setLockedLanguages(requiredLanguageNames(data?.job_required_languages));
      setJobRequiredLanguages(data?.job_required_languages ?? []);
      // The questions come on this payload, not from the public job detail:
      // that route 404s unless the job is public and OPEN, and a candidate who
      // reaches this page without a career page — bulk-uploaded or sourced —
      // would then be refused on submit for a question never shown. A failure
      // here lands in the catch below and blocks the page, rather than
      // rendering a form with the questions silently missing.
      setScreeningQuestions(data?.application_questions || []);
      const filterData = mapApiToForm(data);
      // Show the manual-fill notice only when every value inside draft_data is
      // empty/null (nothing was extracted). Sentinel keys are ignored.
      setExtractionQuality(isDeeplyEmpty(data?.draft_data) ? "partial" : "full");
      form.methods.reset(filterData);
      // Validate immediately so step colours and the Submit gate are accurate.
      form.methods.trigger();
    } catch (err) {
      console.error("Fetch failed:", err);
      setFetchError("Failed to load your information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col px-4 pb-4 sm:px-6 sm:pb-6 md:px-8 md:pb-8 lg:px-10 lg:pb-5 xl:px-12">
      <div className="w-full max-w-5xl xl:max-w-6xl mx-auto">
        {isLoading ? (
          <ReviewSkeleton stepsCount={STEP_CONFIG.length} />
        ) : fetchError ? (
          <Error error={fetchError} onHandleRetry={fetchReviewDetail} />
        ) : (
          <ReviewWizard
            form={form}
            mandatoryFields={mandatoryFields}
            extractionQuality={extractionQuality}
            isFormSubmit={isFormSubmit}
            submitError={submitError}
            currencySign={currencySign}
            languagesSlot={(disabled, required) => (
              <LanguageProficiencyRows
                name="skills.languages"
                disabled={disabled}
                emptyText="No languages added yet."
                lockedLanguages={lockedLanguages}
                // Same two as the apply page — see the note there.
                languageRequired={required?.language}
                levelledLanguages={jobRequiredLanguages}
              />
            )}
            screeningQuestions={screeningQuestions}
            onBack={() => navigate(fromPath, { state: { fromConfirm: true } })}
          />
        )}
      </div>
    </div>
  );
};

export default ConfirmDetails;
